import { isEmpty, isNil, isString } from "lodash-es";
import type { PendingCustomerResource } from "../services/CustomerResourceService";
import { filesize } from "filesize";
import mime from "mime";
import type { FileError } from "@zag-js/file-utils";
import { useTranslation } from "../state/i18n";
import { parse as parseContentDisposition } from "@tinyhttp/content-disposition";
import type { AxiosInstance, AxiosResponse } from "axios";

export const toCustomerFile = (uploadFile: File): PendingCustomerResource => {
  const extension = mime.getExtension(uploadFile.type);

  return {
    name: uploadFile.name.substring(0, uploadFile.name.lastIndexOf(".")),
    fileName: uploadFile.name,
    fileSize: filesize(uploadFile.size),
    extension: isNil(extension) || isEmpty(extension) ? "Unknown" : extension,
    source: URL.createObjectURL(uploadFile),
  };
};

// Some browsers (notably Safari) read the blob after click() returns and cancel the download if it was already revoked
export const BLOB_URL_REVOKE_DELAY_MS = 40_000;

export const triggerBrowserDownload = async (client: AxiosInstance, uri: string, filename?: string) => {
  const response = await client.get(uri, {
    responseType: "blob",
  });

  const resolvedFilename = filename ?? extractFilename(response);

  let a: HTMLAnchorElement | undefined;
  let urlBlob: string | undefined;

  try {
    urlBlob = window.URL.createObjectURL(response.data);

    a = document.createElement("a");
    a.href = urlBlob;
    if (!isNil(resolvedFilename)) {
      a.download = resolvedFilename;
    }

    document.body.appendChild(a);
    a.click();
  } finally {
    if (!isNil(a)) {
      document.body.removeChild(a);
    }
    if (!isNil(urlBlob)) {
      const url = urlBlob;
      setTimeout(() => window.URL.revokeObjectURL(url), BLOB_URL_REVOKE_DELAY_MS);
    }
  }
};

export const extractFilename = (response: AxiosResponse): string | undefined => {
  const headerValue = response.headers["content-disposition"];
  if (!isString(headerValue)) {
    return;
  }

  const contentDisposition = parseContentDisposition(headerValue);
  const filename = contentDisposition.parameters.filename;
  return isString(filename) ? decodeFilename(filename) : undefined;
};

// The API percent-encodes the plain `filename` parameter (e.g. spaces as %20), only `filename*` is decoded by the parser
const decodeFilename = (filename: string): string => {
  try {
    return decodeURIComponent(filename);
  } catch {
    return filename;
  }
};

export type FileUploadError = FileError | "UPLOAD_ABORTED" | "UPLOAD_ERROR";
export const translateFileUploadError = (error: FileUploadError, maxFileSizeInBytes: number): string => {
  const { t, currentLanguage } = useTranslation();

  // Only translate errors that the user can resolve
  switch (error) {
    case "FILE_INVALID_TYPE":
      return t(($) => $.FILE_UPLOAD_VALIDATION_FILE_INVALID_TYPE);
    case "FILE_TOO_LARGE":
      return t(($) => $.FILE_UPLOAD_VALIDATION_FILE_TOO_LARGE, {
        maxFileSize: filesize(maxFileSizeInBytes, { locale: currentLanguage() }),
      });
    case "UPLOAD_ABORTED":
      return t(($) => $.FILE_UPLOAD_VALIDATION_UPLOAD_ABORTED);
    case "TOO_MANY_FILES":
    case "FILE_TOO_SMALL":
    case "FILE_INVALID":
    case "FILE_EXISTS":
    case "UPLOAD_ERROR":
    default:
      return t(($) => $.FILE_UPLOAD_VALIDATION_UNKNOWN, { error });
  }
};
